import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { Credentials, clearCredentials, readCredentials, saveCredentials } from '../storage/credentials';
import { getCurrentUser, JellyfinUser } from '../api/jellyfin';

type SessionValue = {
    credentials: Credentials | null;
    restoring: boolean;
    user: JellyfinUser | null;
    signIn: (credentials: Credentials) => Promise<void>;
    signOut: () => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: PropsWithChildren) {
    const [credentials, setCredentials] = useState<Credentials | null>(null);
    const [restoring, setRestoring] = useState(true);
    const [user, setUser] = useState<JellyfinUser | null>(null);

    useEffect(() => {
        readCredentials().then(setCredentials).finally(() => setRestoring(false));
    }, []);

    useEffect(() => {
        if (!credentials) { setUser(null); return; }
        getCurrentUser(credentials).then(setUser).catch(() => setUser(null));
    }, [credentials]);

    const value = useMemo<SessionValue>(() => ({
        credentials,
        restoring,
        user,
        signIn: async (next) => { await saveCredentials(next); setCredentials(next); },
        signOut: async () => { await clearCredentials(); setCredentials(null); },
    }), [credentials, restoring, user]);

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
    const value = useContext(SessionContext);
    if (!value) throw new Error('useSession must be used inside SessionProvider.');
    return value;
}
